/*
 * Sportsact: nedtælling til afsendelse (snippets/custom-shipping-countdown.liquid).
 * Al beregning sker i dansk tid (Europe/Copenhagen, inkl. sommer-/vintertid) uanset kundens tidszone.
 * Tekster: {tid} = nedtælling (tt:mm:ss), {dag} = næste afsendelsesdag (fx "mandag").
 * Test: ?countdown_now=2026-09-28T13:59:50 (dansk tid) simulerer et tidspunkt; uret kører videre derfra.
 */
(() => {
  if (customElements.get('custom-shipping-countdown')) return;

  const TZ = 'Europe/Copenhagen';
  const DAY = 86400000;
  const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  const partsFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', weekday: 'short',
  });
  const dayNameFormatter = new Intl.DateTimeFormat('da-DK', { timeZone: 'UTC', weekday: 'long' });

  // Dansk "vægur" for et tidspunkt
  function zonedParts(epoch) {
    const p = {};
    partsFormatter.formatToParts(new Date(epoch)).forEach(({ type, value }) => { p[type] = value; });
    return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour, mi: +p.minute, s: +p.second, wd: WEEKDAYS[p.weekday] };
  }

  // Tidspunkt (epoch) for et dansk vægur – håndterer sommer-/vintertid
  function zonedEpoch(y, m, d, h, mi, s = 0) {
    const wall = Date.UTC(y, m - 1, d, h, mi, s);
    let epoch = wall;
    for (let i = 0; i < 3; i++) {
      const p = zonedParts(epoch);
      const offset = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - epoch;
      epoch = wall - offset;
    }
    return epoch;
  }

  // Lukkedage: "24/12, 25/12, 31/12/2026" (dd/mm = hvert år, dd/mm/åååå = kun den dato)
  function parseClosed(text) {
    const recurring = new Set();
    const exact = new Set();
    (text || '').split(/[\s,;]+/).filter(Boolean).forEach((token) => {
      const [d, m, y] = token.split(/[/.-]/).map((n) => parseInt(n, 10));
      if (!d || !m) return;
      if (y) exact.add(`${y < 100 ? 2000 + y : y}-${m}-${d}`);
      else recurring.add(`${m}-${d}`);
    });
    return { recurring, exact };
  }

  const pad = (n) => String(n).padStart(2, '0');
  const escapeHtml = (text) => text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  class CustomShippingCountdown extends HTMLElement {
    connectedCallback() {
      if (this._timer) return;

      const [ch, cm] = (this.dataset.cutoff || '14:00').split(':').map((n) => parseInt(n, 10) || 0);
      this.cutoff = { h: ch, m: cm };
      this.closed = parseClosed(this.dataset.closed);
      this.message = this.querySelector('[data-countdown-message]');

      try {
        this.shippable = JSON.parse(this.querySelector('[data-shippable]')?.textContent || '{}');
      } catch (e) {
        this.shippable = {};
      }

      // Simuleret tid til test
      this.clockOffset = 0;
      const simulated = new URLSearchParams(location.search).get('countdown_now');
      const match = simulated && simulated.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
      if (match) {
        const [, y, m, d, h, mi, s] = match.map((n) => parseInt(n || '0', 10));
        this.clockOffset = zonedEpoch(y, m, d, h, mi, s) - Date.now();
      }

      const form = document.getElementById(this.getAttribute('form'));
      form?.addEventListener('variant:change', (event) => {
        const variant = event.detail.variant;
        this.hidden = !variant || this.shippable[variant.id] !== true;
      });

      this.update();
      this._timer = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this._timer);
      this._timer = null;
    }

    isShipDay(dateUtc) {
      const date = new Date(dateUtc);
      const wd = date.getUTCDay();
      if (wd === 0 || wd === 6) return false;
      const y = date.getUTCFullYear();
      const m = date.getUTCMonth() + 1;
      const d = date.getUTCDate();
      return !this.closed.recurring.has(`${m}-${d}`) && !this.closed.exact.has(`${y}-${m}-${d}`);
    }

    // Returnerer { mode: 'today' | 'tomorrow' | 'later', target?, day? }
    compute(now) {
      const p = zonedParts(now);
      const today = Date.UTC(p.y, p.m - 1, p.d);
      const cutoffToday = zonedEpoch(p.y, p.m, p.d, this.cutoff.h, this.cutoff.m);

      if (this.isShipDay(today) && now < cutoffToday) {
        return { mode: 'today', target: cutoffToday };
      }

      let next = today + DAY;
      for (let i = 0; i < 60 && !this.isShipDay(next); i++) next += DAY;

      const isWeekday = p.wd >= 1 && p.wd <= 5;
      if (isWeekday && next === today + DAY) {
        const n = new Date(next);
        return { mode: 'tomorrow', target: zonedEpoch(n.getUTCFullYear(), n.getUTCMonth() + 1, n.getUTCDate(), this.cutoff.h, this.cutoff.m) };
      }

      const n = new Date(next);
      let day = dayNameFormatter.format(n);
      if ((next - today) / DAY >= 7) day += ` d. ${n.getUTCDate()}/${n.getUTCMonth() + 1}`;
      return { mode: 'later', day };
    }

    update() {
      if (!this.message) return;

      const now = Date.now() + this.clockOffset;
      const state = this.compute(now);

      if (state.mode === 'later') {
        if (this._mode !== 'later' || this._day !== state.day) {
          this.message.textContent = (this.dataset.textLater || '').replace('{dag}', state.day);
          this._mode = 'later';
          this._day = state.day;
        }
        return;
      }

      const left = Math.max(0, Math.floor((state.target - now) / 1000));
      const time = `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;

      if (this._mode !== state.mode) {
        const template = state.mode === 'today' ? this.dataset.textToday : this.dataset.textTomorrow;
        this.message.innerHTML = escapeHtml(template || '').replace('{tid}', '<strong class="custom-countdown__time"></strong>');
        this._time = this.message.querySelector('.custom-countdown__time');
        this._mode = state.mode;
        this._day = null;
      }

      if (this._time) this._time.textContent = time;
    }
  }

  customElements.define('custom-shipping-countdown', CustomShippingCountdown);
  window.SportsactCountdown = { zonedEpoch, zonedParts };
})();
